import { AlertCircle, ArrowLeft, CheckCircle2, Eye, EyeOff, MailCheck } from 'lucide-react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ApiService } from '../services/api';
import React, { useEffect, useState } from 'react';

const MIN_PASSWORD_LENGTH = 8;
const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SEC = 60;

type Step = 'email' | 'code' | 'done';

/**
 * Şifremi unuttum — bağlantı değil, e-postaya 6 haneli kod gider.
 * Kullanıcı kodu bu sayfada girer; hangi alan adında başladıysa orada biter.
 */
export default function ForgotPasswordPage() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [rePassword, setRePassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRePassword, setShowRePassword] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { loading, systemConfig } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async () => {
    await ApiService.forgetPassword(email.trim());
    // Sunucu adresin kayıtlı olup olmadığını söylemez; ekran da söylememeli.
    setCooldown(RESEND_COOLDOWN_SEC);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('E-posta adresinizi giriniz');
      return;
    }
    setSubmitting(true);
    try {
      await requestCode();
      setStep('code');
    } catch (err: any) {
      setError(err?.message || 'İstek gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || submitting) return;
    setError('');
    setInfo('');
    setSubmitting(true);
    try {
      await requestCode();
      setCode('');
      setInfo('Yeni kod gönderildi. Önceki kod artık geçersiz.');
    } catch (err: any) {
      setError(err?.message || 'Kod yeniden gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (code.length !== CODE_LENGTH) {
      setError(`E-postanıza gelen ${CODE_LENGTH} haneli kodu giriniz`);
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Şifre en az ${MIN_PASSWORD_LENGTH} karakter olmalıdır`);
      return;
    }
    if (password !== rePassword) {
      setError('Şifreler eşleşmiyor');
      return;
    }

    setSubmitting(true);
    try {
      await ApiService.resetPassword({ email: email.trim(), code, password, rePassword });
      setStep('done');
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err: any) {
      setError(err?.message || 'Şifre sıfırlanırken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !systemConfig) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Ayardan kapatıldıysa sayfa hiç açılmasın.
  if (systemConfig && systemConfig.isPasswordResetEnabled === false) {
    return <Navigate to="/login" replace />;
  }

  const inputClass =
    'w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium placeholder:text-gray-400';
  const submitClass =
    'w-full bg-purple-600 text-white py-3 px-4 rounded-xl hover:bg-purple-700 focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all shadow-lg shadow-purple-600/20 font-semibold disabled:opacity-50 disabled:cursor-not-allowed';

  const subtitle =
    step === 'email'
      ? 'Hesabınızın e-posta adresini yazın, size 6 haneli doğrulama kodu gönderelim.'
      : step === 'code'
        ? `${email.trim()} adresine gelen kodu ve yeni şifrenizi girin.`
        : '';

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mb-6 flex justify-center">
            <img
              src={systemConfig?.companyLogo || '/logo.svg'}
              alt={systemConfig?.companyName || 'Logo'}
              className="h-24 w-24 object-cover rounded-full"
            />
          </div>

          <div className="text-3xl font-black tracking-tight text-gray-900 mb-2">
            {systemConfig?.companyName || 'Yeni Eğitim Sistemi'}
          </div>

          <h2 className="text-lg font-medium text-gray-500 mb-4">
            {step === 'code' ? 'Yeni Şifre Belirle 🔒' : 'Şifremi Unuttum 🔑'}
          </h2>

          {subtitle && (
            <p className="text-gray-400 text-sm max-w-xs mx-auto leading-relaxed break-words">{subtitle}</p>
          )}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">İşlem Başarısız</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {step === 'email' && (
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <input
              type="email"
              placeholder="ornek@sirketiniz.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              required
              autoFocus
              disabled={submitting}
            />

            <button type="submit" disabled={submitting} className={submitClass}>
              {submitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Gönderiliyor...
                </div>
              ) : (
                'Doğrulama Kodu Gönder'
              )}
            </button>
          </form>
        )}

        {step === 'code' && (
          <>
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <div className="text-sm leading-relaxed text-emerald-800">
                <p>
                  Bu e-posta adresi sistemde kayıtlıysa doğrulama kodu gönderildi. Gelen kutunuzu ve{' '}
                  <strong>spam</strong> klasörünü kontrol edin.
                </p>
                <p className="mt-1 text-xs text-emerald-700">Kod 15 dakika geçerlidir ve yalnızca bir kez kullanılabilir.</p>
                {info && <p className="mt-1 text-xs font-semibold text-emerald-800">{info}</p>}
              </div>
            </div>

            <form onSubmit={handleResetSubmit} className="space-y-4">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                placeholder="6 haneli kod"
                value={code}
                // maxLength KOYMA: "123 456" gibi yapıştırmada boşluk sayılır, 5 hane kalır. Önce rakam dışını at, sonra 6'ya kes.
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
                className={`${inputClass} text-center text-2xl tracking-[0.5em] font-bold`}
                required
                autoFocus
                disabled={submitting}
              />

              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Yeni şifre"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} pr-12`}
                  required
                  autoComplete="new-password"
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  disabled={submitting}>
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <div className="relative">
                <input
                  type={showRePassword ? 'text' : 'password'}
                  placeholder="Yeni şifre (tekrar)"
                  value={rePassword}
                  onChange={(e) => setRePassword(e.target.value)}
                  className={`${inputClass} pr-12`}
                  required
                  autoComplete="new-password"
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowRePassword(!showRePassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  disabled={submitting}>
                  {showRePassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <p className="text-xs text-gray-500">Şifre en az {MIN_PASSWORD_LENGTH} karakter olmalıdır.</p>

              <button type="submit" disabled={submitting} className={submitClass}>
                {submitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Kaydediliyor...
                  </div>
                ) : (
                  'Şifremi Güncelle'
                )}
              </button>
            </form>

            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
              <button
                type="button"
                onClick={handleResend}
                disabled={cooldown > 0 || submitting}
                className="font-semibold text-purple-700 hover:text-purple-800 transition-colors disabled:cursor-not-allowed disabled:text-gray-400"
              >
                {cooldown > 0 ? `Kodu tekrar gönder (${cooldown})` : 'Kodu tekrar gönder'}
              </button>
              <span className="text-gray-300" aria-hidden="true">·</span>
              <button
                type="button"
                onClick={() => { setStep('email'); setCode(''); setError(''); setInfo(''); }}
                disabled={submitting}
                className="font-semibold text-gray-600 hover:text-gray-800 transition-colors"
              >
                Farklı e-posta kullan
              </button>
            </div>
          </>
        )}

        {step === 'done' && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <h3 className="mt-3 text-base font-semibold text-emerald-900">Şifreniz güncellendi</h3>
            <p className="mt-2 text-sm leading-relaxed text-emerald-800">
              Yeni şifrenizle giriş yapabilirsiniz. Giriş ekranına yönlendiriliyorsunuz...
            </p>
          </div>
        )}

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-700 hover:text-purple-800 transition-colors"
          >
            <ArrowLeft size={16} />
            Giriş ekranına dön
          </Link>
        </div>
      </div>
    </div>
  );
}
